import type { ComponentProps, FC } from 'react';
import React, { Fragment, useCallback, useContext, useEffect } from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql, useFragment } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';
import { getTitleContentProperty } from '@confluence/content-topper/entry-points/useTitleContentPropertiesForPublishedPage.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { VIEW_PAGE_BYLINE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceStart } from '@confluence/experience-tracker/entry-points/component.ts';
import { ExternalShareContext } from '@confluence/external-share-context/entry-points/ExternalShareContext.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { usePageSpaceKey } from '@confluence/page-context';
import {
	ReloadType,
	useQuickReloadSubscriber,
} from '@confluence/quick-reload/entry-points/subscription.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop';
import { expValEquals, isExperimentEnabled } from '@confluence/feature-experiments';

import {
	default as ContentHeaderUnifiedQuery,
	type ContentHeaderUnifiedQuery$data as ContentHeaderUnifiedQueryType,
} from '../__generated__/ContentHeaderUnifiedQuery.graphql.ts';
import { DEFAULT_CONTRIBUTORS_FETCH_LIMIT } from '../defaultQueryVariables.js';
import { BYLINE_METRIC } from '../perf.config.js';

import type { ExternalByLineQuery$data as ExternalByLineQueryType } from './__generated__/ExternalByLineQuery.graphql.ts';
import type {
	ByLine_content$data,
	ByLine_content$key,
} from './__generated__/ByLine_content.graphql.ts';
import type {
	ByLineContributors_contentContributors$data,
	ByLineContributors_contentContributors$key,
} from './__generated__/ByLineContributors_contentContributors.graphql.ts';
import { ByLinePlaceholder } from './ByLinePlaceholder.tsx';
import type { QueryType, QueryVariablesType } from './byLineTypes.ts';
import { isFabricEditor, toUserProps } from './byLineUtils.tsx';
import { ByLineWithContributorsComponent } from './ByLineWithContributorsComponent.tsx';
import { ExternalByLineQuery } from './ExternalByLineQuery.graphql.ts';

export const ByLineFragment: GraphQLTaggedNode = graphql`
	fragment ByLine_content on Content
	@argumentDefinitions(useNewContentTopper: { type: "Boolean!", defaultValue: false }) {
		...useReadTime_content
		id
		type
		subType
		history {
			createdDate
			createdBy {
				type
				displayName
				... on KnownUser {
					accountId
				}
				profilePicture {
					path
				}
			}
			ownedBy {
				type
				displayName
				... on KnownUser {
					accountId
				}
				profilePicture {
					path
				}
			}
			lastOwnedBy {
				type
				displayName
				... on KnownUser {
					accountId
				}
				profilePicture {
					path
				}
			}
		}
		version {
			by {
				type
				displayName
				... on KnownUser {
					accountId
				}
				profilePicture {
					path
				}
			}
			friendlyWhen
			when
			number
			contentTypeModified
		}
		contentProperties {
			latest {
				generatedBy
			}
		}
		...RendererContentState_content
		pageTitlePublished: properties(key: "page-title-property-published")
			@include(if: $useNewContentTopper) {
			nodes {
				id
				key
				value
				version {
					number
				}
			}
		}
		editorProperty: properties(key: "editor") {
			nodes {
				id
				key
				value
			}
		}
		metadata {
			frontend {
				coverPictureWidth @include(if: $useNewContentTopper)
			}
		}
	}
`;

export const ByLineContributorsFragment: GraphQLTaggedNode = graphql`
	fragment ByLineContributors_contentContributors on ContentContributors {
		count
		isCurrentUserContributor
		isOwnerContributor
		nodes {
			type
			displayName
			... on KnownUser {
				accountId
			}
			profilePicture {
				path
			}
		}
	}
`;

// WARN: This is temporary code to send analytics event when we would crash the part
// of the ByLine experience when byline user is undefined due to any reason.
// This is done as part of https://ops.internal.atlassian.com/jira/browse/HOT-106424
// investigation. This code should be removed once we know the root cause/impact.
const HOT106424Tracker: FC<{
	content:
		| ExternalByLineQueryType['singleContent']
		| NonNullable<NonNullable<ContentHeaderUnifiedQueryType['content']>['nodes']>[0]
		| ByLine_content$data
		| null
		| undefined;
	contentId: string;
	contentContributors:
		| QueryType['contentContributors']
		| ByLineContributors_contentContributors$data
		| null
		| undefined;
	isExternalShareRequest: boolean;
}> = ({ content, contentId, contentContributors, isExternalShareRequest }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		const byUser = content?.version?.by;
		const createdBy = content?.history?.createdBy;
		const hasEmptyContributors = (contentContributors?.nodes || []).some((node) => !node);
		const contentHistory = content?.history;
		const createdDate = content?.history?.createdDate;

		if (hasEmptyContributors || !createdBy || !byUser) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: 'byLine',
					action: 'byLineUserEmpty',
					actionSubject: 'content',
					attributes: {
						contentId,
						isExternalShareRequest,
						hasEmptyContributors,
						hasCreatedBy: Boolean(createdBy),
						hasByUser: Boolean(byUser),
						hasContent: Boolean(content),
						hasHistory: Boolean(contentHistory),
						hasCreatedDate: Boolean(createdDate),
					},
				},
			}).fire();
		}
	}, [content, contentId, contentContributors, createAnalyticsEvent, isExternalShareRequest]);

	return null;
};

type ByLineProps = {
	/**
	 * ID of the content to render
	 */
	contentId: string;
	/**
	 * Version to render
	 */
	versionOverride?: number | null;
	/**
	 * Control which version should be rendered for embedded content (attachments, "page-include" macro, etc.).
	 * 'version-at-save': Render the exact version when page was saved.
	 * 'current': Default. Render the latest version.
	 */
	embeddedContentRender?: string | null;
	/**
	 * Determines if we should display reactions in the byline
	 */
	shouldShowBylineReactions?: boolean;
	/**
	 * Content type to render
	 */
	contentType?: string | null;
	/**
	 * Content space id
	 */
	spaceId?: string | null;
	/**
	 * Content sub type to render
	 */
	contentSubType?: string | null;
};

type PassThroughProps = Pick<
	ComponentProps<typeof ByLineWithContributorsComponent>,
	'hasByLineContributors' | 'hasByLineExtensions'
>;

type ByLineFromFragmentProps = ByLineProps &
	PassThroughProps & {
		contentFragmentRef: ByLine_content$key;
		contentContributors?: ByLineContributors_contentContributors$key | null;
	};

export const ByLineFromFragment: FC<ByLineFromFragmentProps> = ({
	contentFragmentRef,
	contentContributors,
	contentId,
	shouldShowBylineReactions,
	contentType,
	spaceId,
	contentSubType,
	...passThroughProps
}) => {
	const content = useFragment(ByLineFragment, contentFragmentRef);
	const contentContributorsData = useFragment(
		ByLineContributorsFragment,
		contentContributors ?? null,
	);

	if (!content) {
		return null;
	}

	const author = toUserProps(content.history?.createdBy);
	const owner =
		content.history?.ownedBy && (content.type === 'page' || content.type === 'blogpost')
			? toUserProps(content.history?.ownedBy)
			: null;
	const by = toUserProps(content.version?.by);
	const contributorsCount = contentContributorsData?.count || 0;
	const isOwnerContributor = contentContributorsData?.isOwnerContributor || false;
	const editorVersion = content.editorProperty?.nodes?.find(
		(property) => property?.key === 'editor',
	);
	const isFabricPage = editorVersion?.value?.includes('v2') || false;
	const { titleContentProperties } = getTitleContentProperty({ content: { nodes: [content] } });
	const generatedBy = content.contentProperties?.latest?.generatedBy;

	return (
		<ErrorBoundary attribution={Attribution.BACKBONE}>
			<ExperienceStart name={VIEW_PAGE_BYLINE_EXPERIENCE} id={contentId} />
			<PageSegmentLoadStart key={contentId} metric={BYLINE_METRIC} />
			<ByLineWithContributorsComponent
				contentId={contentId}
				author={author}
				owner={owner}
				contributorsCount={contributorsCount}
				isOwnerContributor={isOwnerContributor}
				isFabricPage={isFabricPage}
				shouldShowBylineReactions={shouldShowBylineReactions}
				contentSubType={contentSubType}
				spaceId={spaceId}
				contentType={contentType}
				titleContentProperties={titleContentProperties}
				generatedBy={generatedBy}
				connectionAncestors={null}
				createdDate={content.history?.createdDate || null}
				contentFragmentRef={content}
				version={{
					friendlyWhen: content.version?.friendlyWhen || '',
					when: content.version?.when || null,
					number: Number(content.version?.number || ''),
					by,
					contentTypeModified: content.version?.contentTypeModified || false,
				}}
				{...passThroughProps}
			/>
			<PageSegmentLoadEnd
				key={contentId}
				metric={BYLINE_METRIC}
				customData={{ contributorsCount }}
			/>
			<HOT106424Tracker
				content={content}
				contentId={contentId}
				contentContributors={contentContributorsData}
				isExternalShareRequest={false}
			/>
		</ErrorBoundary>
	);
};

export const ByLine: FC<ByLineProps & PassThroughProps> = ({
	contentId,
	versionOverride,
	embeddedContentRender,
	shouldShowBylineReactions,
	contentType,
	spaceId,
	contentSubType,
	...passThroughProps
}) => {
	const [spaceKey] = usePageSpaceKey();
	const { isExternalShareRequest } = useContext(ExternalShareContext);

	const { data, loading, error, refetch } = useQueryInterop<
		QueryType,
		QueryVariablesType & { cacheBuster?: string }
	>(isExternalShareRequest ? ExternalByLineQuery : ContentHeaderUnifiedQuery, {
		variables: {
			contentId,
			spaceKey,
			// Don't omit versionOverride, embeddedContentRender and limit
			// It's an Apollo bug that if preloader these needs to be match preloaded query
			// Also doesn't work if removing them from preloader variables HOT-109035
			versionOverride: versionOverride || null,
			embeddedContentRender: embeddedContentRender || null,
			limit: DEFAULT_CONTRIBUTORS_FETCH_LIMIT,
			useNewContentTopper: true,
			isMarkdownModeEnabled: expValEquals('cc-markdown-mode', 'isEnabled', true),
			includeDevDocsBylineConnection:
				isExperimentEnabled('confluence_1p_and_3p_connection_byline_experiment') &&
				isExperimentEnabled('confluence_dev_docs'),
		},
		context: {
			allowOnExternalPage: true,
			single: true,
		},
	});

	const reload = useCallback(
		// Forcing refetch by passing dummy variable. Known Apollo bug (ノಠ益ಠ)ノ彡┻━┻, see page below:
		// https://pug.jira-dev.com/wiki/spaces/~683304838/pages/5971970200/Apollo+Client+-+weird+refetch+behavior
		// Reason using static cacheBuster: "":
		// ContentTitle component also makes ContentHeaderUnifiedQuery and refetch.
		// If using different cache buster values, the refetch in each component will make its owen refetch network call.
		// So setting a static value for this and ContentTitle component to make refetch network call only once.
		// And a static cache buster value can also force refetch to ignore cache of original query.
		() => refetch({ contentId, cacheBuster: '' }),

		[refetch, contentId],
	);

	useQuickReloadSubscriber({
		name: 'byline',
		types: [ReloadType.content],
		reload,
	});

	const renderByLine = () => {
		if (loading) {
			return <ByLinePlaceholder />;
		}

		const content = isExternalShareRequest
			? (data as ExternalByLineQueryType)?.singleContent
			: (data as ContentHeaderUnifiedQueryType)?.content?.nodes?.[0];

		if (error) {
			if (isUnauthorizedError(error)) {
				markErrorAsHandled(error);
				return null;
			} else {
				// TODO: unknown errors should have a nicer UI within `<ErrorDisplay>`, but we don't have such UI for now
				// so returning empty `<ErrorDisplay>` here.
				return <ErrorDisplay error={error} />;
			}
		} else if (!content) {
			return null;
		}

		const author = toUserProps(content?.history?.createdBy);
		const owner =
			content?.history?.ownedBy && (content?.type === 'page' || content?.type === 'blogpost')
				? toUserProps(content?.history?.ownedBy)
				: null;
		const by = toUserProps(content?.version?.by);
		const contributorsCount = data?.contentContributors?.count || 0;
		const isOwnerContributor = data?.contentContributors?.isOwnerContributor || false;

		const isFabricPage = !isExternalShareRequest ? isFabricEditor(data) : false;
		const { titleContentProperties } = getTitleContentProperty(data);
		const contentHeaderContent = !isExternalShareRequest
			? (data as ContentHeaderUnifiedQueryType)?.content?.nodes?.[0]
			: null;
		const generatedBy = contentHeaderContent?.contentProperties?.latest?.generatedBy;
		const connectionAncestors = contentHeaderContent?.ancestors ?? null;

		const componentProps = {
			contentId,
			author,
			owner,
			contributorsCount,
			isOwnerContributor,
			isFabricPage,
			shouldShowBylineReactions,
			contentSubType,
			spaceId,
			contentType,
			titleContentProperties,
			generatedBy,
			connectionAncestors,
			createdDate: content?.history?.createdDate || null,
			version: {
				friendlyWhen: content?.version?.friendlyWhen || '',
				// @ts-expect-error -- `ExternalByLineQuery` does not query for `when`; this should later be refactored to Relay fragments in the actual components
				when: content?.version?.when || null,
				number: Number(content?.version?.number || ''),
				by,
				contentTypeModified: content?.version?.contentTypeModified || false,
			},
			...passThroughProps,
		};

		return (
			<Fragment>
				<ByLineWithContributorsComponent {...componentProps} />
				<PageSegmentLoadEnd
					key={contentId}
					metric={BYLINE_METRIC}
					customData={{ contributorsCount }}
				/>
				<HOT106424Tracker
					content={content}
					contentId={contentId}
					contentContributors={data?.contentContributors}
					isExternalShareRequest={isExternalShareRequest}
				/>
			</Fragment>
		);
	};

	return (
		<ErrorBoundary attribution={Attribution.BACKBONE}>
			<ExperienceStart name={VIEW_PAGE_BYLINE_EXPERIENCE} id={contentId} />
			<PageSegmentLoadStart key={contentId} metric={BYLINE_METRIC} />
			{renderByLine()}
		</ErrorBoundary>
	);
};
